import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import Breadcrumb from '~/components/Breadcrumbs/Breadcrumb';
import { MdCheck } from 'react-icons/md';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { fetchMedicalProfessionals } from '~/services/medical-professionals';
import AddProfessionalModal from '~/components/MedicalProfessionalDetail/modals/AddProfessional';
import { formatDateIfValid } from '~/utils';
import { Pagination } from '~/components/Pagination';
import { useDebouncedURLSearchParams } from '~/hooks/useDebouncedUrlSearchParams';
import { FaSearch } from 'react-icons/fa';

interface MedicalProfessional {
    id: number;
    person: string;
    is_active: boolean;
    update_date: string;
    created_date: string;
    status: string;
    verified_patient_count: number;
    unverified_patient_count: number;
    referrals: number;
    verified: boolean;
    approved_by_person_id: number;
    added_by_person_id: number;
}

const columnHelper = createColumnHelper<MedicalProfessional>();

const columns = [
    columnHelper.accessor((row) => `${row.person}`, {
        id: 'name',
        header: () => 'Name',
        cell: (info) => (
            <Link
                className='font-bold text-primary'
                to={`/medical-professionals/${info.row.original.id}`}
            >
                {info.renderValue()}
            </Link>
        ),
    }),
    columnHelper.accessor(
        (props) =>
            props.update_date
                ? formatDateIfValid(props.update_date, 'MM/dd/yy')
                : '',
        {
            id: 'updated_date',
            header: () => 'Updated',
            cell: (props) => props.renderValue(),
        }
    ),
    columnHelper.accessor('is_active', {
        header: 'Status',
        cell: (info) => (
            <div
                className={`inline-flex rounded-full px-3 py-1 text-sm hover:bg-opacity-90 ${
                    info.getValue() === true
                        ? 'bg-primary'
                        : info.getValue() === false
                        ? 'bg-whiten text-secondary'
                        : ''
                }`}
            >
                {info.getValue() === true ? 'Active' : 'Inactive'}
            </div>
        ),
    }),
    columnHelper.accessor('approved_by_person_id', {
        header: 'Verified',
        cell: (info) => (info.getValue() ? <MdCheck /> : ''),
    }),
    columnHelper.accessor('verified_patient_count', {
        header: 'Verified Patients',
        cell: (info) => info.renderValue(),
    }),
    columnHelper.accessor('unverified_patient_count', {
        header: 'Unverified Patients',
        cell: (info) => info.renderValue(),
    }),
    columnHelper.accessor('referrals', {
        header: 'Referrals',
        cell: (info) => info.renderValue(),
    }),
];

const MedicalProfessionals: React.FC = () => {
    const queryClient = useQueryClient();
    const [tableData, setTableData] = useState([]);
    const [pages, setPages] = useState<number>(1);

    const [filters, setFilters, results, isLoading] =
        useDebouncedURLSearchParams(
            {
                page: 1,
                status: '',
                verificationStatus: '',
                name: '',
            },
            'medical-professional',
            fetchMedicalProfessionals,
            [{ key: 'name', debounceTime: 500 }]
        );

    const table = useReactTable({
        data: tableData,
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    useEffect(() => {
        if (results) {
            const dataFetched = results;
            setTableData(dataFetched.results);
            setPages(Math.ceil(dataFetched.count / 20));
        }
    }, [results, isLoading]);

    const handleFilterChange = (
        key: keyof typeof filters,
        value: string | number
    ) => {
        setFilters((prev) => ({
            ...prev,
            [key]: value,
            ...(key !== 'page' ? { page: 1 } : {}),
        }));
    };

    return (
        <>
            <Breadcrumb pageName='Medical Professionals' />

            <section className='data-table-common rounded-sm border border-stroke bg-white py-4 shadow-default dark:border-strokedark dark:bg-boxdark'>
                <div className='flex items-center justify-between gap-4 px-8 pb-4'>
                    <div className='flex items-center justify-between gap-4'>
                        <div className='relative w-full min-w-[200px] max-w-[250px]'>
                            <input
                                type='text'
                                value={filters.name}
                                onChange={(e) => {
                                    handleFilterChange('name', e.target.value);
                                }}
                                className='w-full rounded-md border border-stroke px-3 py-2 outline-none focus:border-primary'
                                placeholder='Name or Email'
                            />
                            <FaSearch
                                color='gray'
                                className='absolute right-3 top-1/2 -translate-y-1/2'
                            />
                        </div>
                        <div className='w-full min-w-[125px] max-w-[175px]'>
                            <select
                                name='status'
                                id='status'
                                className='w-full rounded-md border border-stroke px-3 py-2 outline-none focus:border-primary'
                                onChange={(e) => {
                                    handleFilterChange(
                                        'status',
                                        e.target.value
                                    );
                                }}
                                value={filters.status}
                            >
                                <option value=''>Status</option>
                                <option value='active'>Active</option>
                                <option value='inactive'>Inactive</option>
                            </select>
                        </div>
                        <div className='w-full min-w-[180px]'>
                            <select
                                name='verified-status'
                                id='verified-status'
                                className='w-full rounded-md border border-stroke px-3 py-2 outline-none focus:border-primary'
                                onChange={(e) => {
                                    handleFilterChange(
                                        'verificationStatus',
                                        e.target.value
                                    );
                                }}
                                value={filters.verificationStatus}
                            >
                                <option value=''>Verification Status</option>
                                <option value='verified'>Verified</option>
                                <option value='unverified'>Unverified</option>
                            </select>
                        </div>
                    </div>

                    <AddProfessionalModal
                        onCreated={() =>
                            queryClient.invalidateQueries({
                                predicate: (q) =>
                                    Array.isArray(q.queryKey) &&
                                    q.queryKey.includes('medical-professional'),
                            })
                        }
                    />
                </div>

                <div
                    className='flex items-center justify-between border-t border-stroke px-8 py-1'
                    style={{ fontSize: '12px' }}
                >
                    <p>Showing {Math.min(20, (results?.count || 0) - (filters.page - 1) * 20)} of {results?.count || 0} results</p>
                </div>

                <div className='overflow-x-auto'>
                    <table className='datatable-table datatable-one w-full table-auto border-collapse break-words px-4 md:px-8 2xl:table-fixed'>
                        <thead className='border-separate px-4'>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr
                                    className='border-t border-stroke bg-whiten'
                                    style={{ fontSize: '14px' }}
                                    key={headerGroup.id}
                                >
                                    {headerGroup.headers.map((header) => (
                                        <th
                                            style={{
                                                paddingTop: '16px',
                                                paddingBottom: '16px',
                                            }}
                                            key={header.id}
                                        >
                                            <div className='flex items-center'>
                                                <span>
                                                    {header.isPlaceholder
                                                        ? null
                                                        : flexRender(
                                                              header.column
                                                                  .columnDef
                                                                  .header,
                                                              header.getContext()
                                                          )}
                                                </span>
                                                <div className='ml-2 inline-flex flex-col space-y-[2px]'>
                                                    <span className='inline-block'>
                                                        <svg
                                                            xmlns='http://www.w3.org/2000/svg'
                                                            width='17'
                                                            height='16'
                                                            viewBox='0 0 17 16'
                                                            fill='none'
                                                        >
                                                            <path
                                                                d='M8.5 14.6666L4.5 10.6666L5.45 9.71659L8.5 12.7666L11.55 9.71659L12.5 10.6666L8.5 14.6666ZM5.46667 6.26659L4.5 5.33325L8.5 1.33325L12.5 5.33325L11.5333 6.26659L8.5 3.23325L5.46667 6.26659Z'
                                                                fill='#131212'
                                                            />
                                                        </svg>
                                                    </span>
                                                </div>
                                            </div>
                                        </th>
                                    ))}
                                    <th
                                        style={{
                                            paddingTop: '16px',
                                            paddingBottom: '16px',
                                        }}
                                    ></th>
                                </tr>
                            ))}
                        </thead>
                        <tbody>
                            {isLoading
                                ? Array.from({ length: 20 }).map((_, index) => (
                                      <tr key={index}>
                                          {columns.map((_column, colIndex) => (
                                              <td
                                                  key={colIndex}
                                                  className='border-b border-stroke px-4 py-2 dark:border-strokedark'
                                              >
                                                  <div className='max-w-sm animate-pulse'>
                                                      <div className='h-2 max-w-[360px] rounded-full bg-gray-4 dark:bg-gray-5'></div>
                                                  </div>
                                              </td>
                                          ))}
                                          <td className='border-b border-stroke px-4 py-2 dark:border-strokedark'>
                                              <div className='float-right w-full max-w-[10px] animate-pulse'>
                                                  <div className='h-[10px] max-w-[360px] rounded-full bg-gray-4 dark:bg-gray-5'></div>
                                              </div>
                                          </td>
                                      </tr>
                                  ))
                                : table.getRowModel().rows.map((row) => (
                                      <tr
                                          className='border-t border-stroke'
                                          key={row.id}
                                      >
                                          {row.getVisibleCells().map((cell) => (
                                              <td
                                                  style={{
                                                      paddingTop: '16px',
                                                      paddingBottom: '16px',
                                                  }}
                                                  key={cell.id}
                                              >
                                                  {flexRender(
                                                      cell.column.columnDef
                                                          .cell,
                                                      cell.getContext()
                                                  )}
                                              </td>
                                          ))}
                                          <td
                                              className='text-right'
                                              style={{
                                                  paddingTop: '16px',
                                                  paddingBottom: '16px',
                                              }}
                                          ></td>
                                      </tr>
                                  ))}
                        </tbody>
                    </table>
                </div>

                <Pagination
                    page={filters.page}
                    pages={pages}
                    setPage={(val: number) => handleFilterChange('page', val)}
                    totalCount={results?.count}
                />
            </section>
        </>
    );
};

export default MedicalProfessionals;
